import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiHeart,
  FiActivity,
  FiMoon,
  FiSun,
  FiAlertTriangle,
  FiDroplet,
  FiHome
} from "react-icons/fi";
import {
  FaRunning,
  FaSwimmer,
  FaRegLaughBeam,
  FaRegMoon,
} from "react-icons/fa";
import { GiStrong, GiMeditation } from "react-icons/gi";
import { Helmet } from "react-helmet-async";

const HormonalExerciseGuide = () => {
  const [activePhase, setActivePhase] = useState("menstrual");

  // Phase data with enhanced visuals
  const phases = {
    menstrual: {
      title: "Menstrual Phase",
      days: "Days 1-5",
      color: "bg-purple-100",
      borderColor: "border-purple-300",
      icon: <FaRegMoon className="text-purple-500 text-3xl" />,
      description:
        "Your body is renewing itself. Honor this natural reset with gentle movement and self-compassion.",
      exercises: [
        {
          name: "Restorative Yoga",
          icon: <GiMeditation className="text-2xl" />,
          benefit: "Soothes cramps and calms the nervous system",
        },
        {
          name: "Nature Walks",
          icon: <FaRunning className="text-2xl" />,
          benefit: "Gentle movement boosts endorphins",
        },
        {
          name: "Breath Work",
          icon: "🧘‍♀️",
          benefit: "Reduces bloating and stress",
        },
      ],
      careTips: [
        "Try warm epsom salt baths for muscle relief",
        "Dark chocolate (70%+) helps with magnesium needs",
        "Dark chocolate (70%+) helps with magnesium needs",
        "Listen to your body's need for extra sleep",
      ],
      expertTip:
        "This is your body's natural recovery week. Think of it as your 'deload' phase in your monthly cycle.",
    },
    follicular: {
      title: "Follicular Phase",
      days: "Days 6-13",
      color: "bg-blue-100",
      borderColor: "border-blue-300",
      icon: <FiSun className="text-yellow-500 text-3xl" />,
      description:
        "Your energy is rising like the morning sun! Capitalize on this natural boost for challenging workouts.",
      exercises: [
        {
          name: "Strength Training",
          icon: <GiStrong className="text-2xl" />,
          benefit: "Muscle growth potential peaks",
        },
        {
          name: "HIIT Workouts",
          icon: "⚡",
          benefit: "Explosive power and endurance",
        },
        {
          name: "Dance Fitness",
          icon: "💃",
          benefit: "Joyful movement matches rising mood",
        },
      ],
      careTips: [
        "Perfect time to try new fitness classes",
        "Increase protein intake for muscle repair",
        "Challenge yourself with progressive overload",
      ],
      expertTip:
        "Your pain tolerance increases now—great time to attempt personal records safely!",
    },
    ovulation: {
      title: "Ovulation Phase",
      days: "Day 14 (approx.)",
      color: "bg-pink-100",
      borderColor: "border-pink-300",
      icon: <FaRegLaughBeam className="text-pink-500 text-3xl" />,
      description:
        "You're at your physical peak! Coordination, strength, and confidence all align beautifully.",
      exercises: [
        {
          name: "Heavy Lifting",
          icon: "🏋️‍♀️",
          benefit: "5-10% strength increase possible",
        },
        {
          name: "Sprint Training",
          icon: "🏃‍♀️",
          benefit: "Explosive power peaks",
        },
        {
          name: "Sports",
          icon: "🎯",
          benefit: "Reaction time and accuracy optimal",
        },
      ],
      careTips: [
        "Be mindful of joint flexibility",
        "Hydration is key for temperature regulation",
        "Schedule important competitions now",
      ],
      expertTip:
        "Your natural charisma peaks too—great time for team sports or partner workouts!",
    },
    luteal: {
      title: "Luteal Phase",
      days: "Days 15-28",
      color: "bg-red-100",
      borderColor: "border-red-300",
      icon: <FiHeart className="text-red-400 text-3xl" />,
      description:
        "Your body needs more mindful movement. Think 'strong not strained' during this phase.",
      exercises: [
        {
          name: "Pilates",
          icon: "🩰",
          benefit: "Builds strength without strain",
        },
        {
          name: "Swimming",
          icon: <FaSwimmer className="text-2xl" />,
          benefit: "Soothes bloating and joint pressure",
        },
        {
          name: "Hiking",
          icon: "🥾",
          benefit: "Nature connection boosts mood",
        },
      ],
      careTips: [
        "Increase magnesium-rich foods",
        "Try evening workouts if mornings feel hard",
        "Add 30s extra rest between sets",
      ],
      expertTip: "Your metabolism increases by 8-16%—nourish yourself well!",
    },
  };

  return (
    <div className="w-full font-nunito min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8">
<Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Cycle-Synced Workouts - FlowHearts | Hormone-Based Fitness</title>
    <meta
      name="description"
      content="Optimize fitness with FlowHearts' cycle-synced workouts, tailored to menstrual phases for hormone balancing, strength, and recovery."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/hormonal-exercise" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/hormonal-exercise" />
    <meta
      property="og:title"
      content="Cycle-Synced Workouts - FlowHearts | Hormone-Based Fitness"
    />
    <meta
      property="og:description"
      content="Align your workouts with your menstrual cycle using FlowHearts' cycle-synced exercises, designed for hormone health and optimal fitness."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/hormonal-exercise-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/hormonal-exercise" />
    <meta
      property="twitter:title"
      content="Cycle-Synced Workouts - FlowHearts | Hormone-Based Fitness"
    />
    <meta
      property="twitter:description"
      content="Discover cycle-synced workouts with FlowHearts, tailoring exercise to menstrual phases for better hormone balance and fitness results."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/hormonal-exercise-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Cycle-Synced Workouts",
          "url": "https://flowhearts.com/hormonal-exercise",
          "description": "Sync workouts with menstrual cycle phases using FlowHearts' guide for hormone-balanced fitness and recovery.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto px-4 md:px-6">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>

        <div className="bg-white bg-opacity-90 backdrop-blur-lg rounded-3xl p-8 shadow-xl border border-white">
          {/* Header with decorative elements */}
          <div className="text-center mb-8 relative">
            <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-pink-200 opacity-30"></div>
            <div className="absolute -bottom-6 -right-6 w-16 h-16 rounded-full bg-purple-200 opacity-30"></div>
            <h2 className="text-4xl font-cormorant font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600 relative z-10">
              Hormonal Phase-Based Exercises
            </h2>
            <p className="text-lg text-purple-700 max-w-2xl mx-auto relative z-10">
              Work <span className="text-pink-500 font-medium">with</span> your
              cycle, not against it
            </p>
          </div>

          {/* Phase Selector - Enhanced */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {Object.entries(phases).map(([phaseKey, phase]) => (
              <button
                key={phaseKey}
                onClick={() => setActivePhase(phaseKey)}
                className={`p-4 rounded-xl transition-all flex flex-col items-center relative overflow-hidden group ${
                  phase.color
                } ${
                  activePhase === phaseKey
                    ? `border-2 ${phase.borderColor} scale-[1.02]`
                    : "hover:bg-opacity-70"
                }`}
              >
                <div className="absolute inset-0 bg-white bg-opacity-20 group-hover:bg-opacity-30 transition-all"></div>
                <div className="relative z-10">
                  <div className="mb-3 flex justify-center items-center">
                    {phase.icon}
                  </div>
                  <h3 className="font-semibold text-purple-800 text-center">
                    {phase.title}
                  </h3>
                  <p className="text-sm text-purple-600 text-center">
                    {phase.days}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Phase Overview Card */}
            <div className="lg:col-span-2">
              <div
                className={`rounded-2xl p-6 shadow-lg relative overflow-hidden ${phases[activePhase].color} border ${phases[activePhase].borderColor}`}
              >
                <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-white bg-opacity-20"></div>
                <div className="relative z-10">
                  <div className="flex items-start mb-4">
                    <div className="mr-4">{phases[activePhase].icon}</div>
                    <div>
                      <div className="flex items-baseline">
                        <h3 className="text-2xl font-bold text-purple-800 mr-2">
                          {phases[activePhase].title}
                        </h3>
                        <span className="text-purple-600">
                          {phases[activePhase].days}
                        </span>
                      </div>
                      <p className="text-purple-700 mt-2">
                        {phases[activePhase].description}
                      </p>
                    </div>
                  </div>

                  {/* Expert Tip */}
                  <div className="bg-white bg-opacity-70 p-4 rounded-lg backdrop-blur-sm mt-4 border border-white border-opacity-50">
                    <div className="flex items-start">
                      <div className="bg-pink-100 p-2 rounded-full mr-3">
                        <FiHeart className="text-pink-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800 mb-1">
                          Cycle Wisdom
                        </h4>
                        <p className="text-purple-700">
                          {phases[activePhase].expertTip}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exercise Recommendations */}
              <div className="mt-6">
                <h3 className="text-xl font-bold mb-4 text-purple-800 flex items-center">
                  <span className="w-4 h-4 bg-pink-500 rounded-full mr-2"></span>
                  Ideal Exercises
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {phases[activePhase].exercises.map((exercise, index) => (
                    <div
                      key={index}
                      className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center mb-3">
                        <div className="bg-purple-100 p-2 rounded-full mr-3">
                          {exercise.icon}
                        </div>
                        <h4 className="font-semibold text-purple-800">
                          {exercise.name}
                        </h4>
                      </div>
                      <p className="text-sm text-purple-600 pl-11">
                        {exercise.benefit}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Care Tips */}
            <div className="space-y-6">
              {/* Self-Care Card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold mb-4 text-purple-800 flex items-center">
                  <span className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></span>
                  Self-Care Essentials
                </h3>
                <ul className="space-y-3">
                  {phases[activePhase].careTips.map((tip, index) => (
                    <li
                      key={index}
                      className="flex items-start text-purple-700 pl-2"
                    >
                      <span className="bg-pink-100 text-pink-500 rounded-full w-5 h-5 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        {index + 1}
                      </span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Nutrition & Hydration */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 shadow-sm border border-blue-100">
                <h3 className="text-xl font-bold mb-4 text-purple-800 flex items-center">
                  <span className="w-4 h-4 bg-blue-500 rounded-full mr-2"></span>
                  Nourishment Guide
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                      <FiDroplet className="text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-medium text-purple-800">Hydration</h4>
                      <p className="text-sm text-purple-600">
                        {activePhase === "luteal"
                          ? "Increase to 3L water daily with electrolytes"
                          : "Aim for 2-2.5L with lemon or mint for freshness"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-pink-100 p-2 rounded-full mr-3 flex-shrink-0">
                      <FiHeart className="text-pink-500" />
                    </div>
                    <div>
                      <h4 className="font-medium text-purple-800">
                        Key Nutrients
                      </h4>
                      <p className="text-sm text-purple-600">
                        {activePhase === "menstrual"
                          ? "Iron-rich foods + vitamin C for absorption"
                          : activePhase === "follicular"
                          ? "Lean proteins for muscle repair"
                          : activePhase === "ovulation"
                          ? "Omega-3s to support hormone balance"
                          : "Complex carbs + magnesium for mood support"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sample Exercise Visual */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                <div className="h-48 bg-gradient-to-br from-pink-100 to-purple-100 flex flex-col items-center justify-center text-purple-700">
                  <div className="text-5xl mb-2">
                    {phases[activePhase].exercises[0].icon}
                  </div>
                  <p className="font-medium">
                    {phases[activePhase].exercises[0].name}
                  </p>
                  <p className="text-sm opacity-80 mt-1">
                    Perfect for {activePhase} phase
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Scientific Insight */}
          <div className="mt-10 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 shadow-sm border border-purple-100">
            <div className="flex items-start">
              <div className="bg-purple-100 p-2 rounded-full mr-4 flex-shrink-0">
                <FiActivity className="text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-purple-800 mb-2">
                  The Science Behind Cycle Syncing
                </h3>
                <p className="text-purple-700">
                  Research shows estrogen improves muscle recovery (up to 20%
                  faster in follicular phase) and progesterone enhances fat
                  metabolism. A 2021 study found women who trained cyclically
                  gained 15% more strength than those following static programs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Educational Section */}
      <div className="max-w-8xl mx-auto px-4 md:px-6 mt-12">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* How to Sync Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiActivity className="mr-3 text-pink-500" />
              How to Sync Your Workouts with Your Cycle
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Track Your Phases
                </h3>
                <p className="text-gray-700 text-center">
                  Use an app to monitor your menstrual cycle and identify each phase accurately
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Plan Your Sessions
                </h3>
                <p className="text-gray-700 text-center">
                  Align exercises with hormonal changes for optimal results
                </p>
              </div>
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-green-700 mb-2">
                  Adjust and Listen
                </h3>
                <p className="text-gray-700 text-center">
                  Modify based on how your body feels in each phase
                </p>
              </div>
            </div>
          </div>

          {/* Expanded Science Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiHeart className="mr-3 text-purple-500" />
              The Science of Hormone-Optimized Fitness
            </h2>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Hormonal Influences on Performance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2">
                    Estrogen's Role
                  </p>
                  <p className="text-gray-700">
                    Boosts muscle building and energy levels during follicular phase
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2">
                    Progesterone Effects
                  </p>
                  <p className="text-gray-700">
                    Increases body temperature and recovery needs in luteal phase
                  </p>
                </div>
              </div>
              <p className="text-gray-700">
                Modern research supports cycle syncing for better gains and reduced injury risk in women's fitness routines.
              </p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiSun className="mr-3 text-green-500" />
              Benefits of Cycle-Based Workouts
            </h2>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
              <p className="text-gray-700 mb-4">
                Adopting hormone balancing exercises can transform your fitness journey, aligning movement with your natural rhythms for sustainable results.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FiHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Enhanced recovery and reduced burnout in women's hormone health</span>
                </li>
                <li className="flex items-start">
                  <FiHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Improved strength gains through strategic period phase exercises</span>
                </li>
                <li className="flex items-start">
                  <FiHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Better mood and motivation with natural cycle training</span>
                </li>
                <li className="flex items-start">
                  <FiHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Lower injury risk by respecting hormonal fluctuations</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Myths Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiAlertTriangle className="mr-3 text-red-500" />
              Debunking Myths About Cycle Syncing Workouts
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: It's Too Complicated
                </h3>
                <p className="text-gray-700">
                  Reality: Simple tracking makes hormone-optimized fitness accessible for daily routines
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Only for Athletes
                </h3>
                <p className="text-gray-700">
                  Reality: Benefits everyone pursuing better women's hormone health through movement
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Cycles Are Too Irregular
                </h3>
                <p className="text-gray-700">
                  Reality: Even variable cycles can guide natural cycle training adjustments
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: No Scientific Backing
                </h3>
                <p className="text-gray-700">
                  Reality: Growing research supports menstrual phase fitness benefits
                </p>
              </div>
            </div>
          </div>

          {/* Tips Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiMoon className="mr-3 text-blue-500" />
              Practical Tips for Hormone Balancing Exercises
            </h2>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
              <p className="text-gray-700 mb-4">
                Incorporate these strategies to make cycle syncing workouts a natural part of your lifestyle.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Journal Progress</h3>
                  <p className="text-gray-700 text-sm">
                    Note how exercises feel in different phases for personalized adjustments
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Mix Modalities</h3>
                  <p className="text-gray-700 text-sm">
                    Combine strength and cardio based on energy levels
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Nutrition Alignment</h3>
                  <p className="text-gray-700 text-sm">
                    Match meals to support workout intensity in each phase
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Rest Intentionally</h3>
                  <p className="text-gray-700 text-sm">
                    Schedule recovery days during lower energy periods
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Help Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiAlertTriangle className="mr-3 text-yellow-500" />
              When to Seek Professional Guidance
            </h2>
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <p className="text-gray-700 mb-4">
                While self-guided period phase exercises are beneficial, certain situations call for expert input.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Persistent fatigue despite cycle adjustments</span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Unusual pain during workouts</span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Hormonal imbalances affecting daily life</span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Planning intense training programs</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Combine this guide with professional advice for comprehensive women's hormone health support.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HormonalExerciseGuide;